import { ComponentProps, FC, useMemo } from 'react';

import classNames from 'classnames';

import './PanelLayout.scss';

import { TestMetadata } from '../types';

export interface CssGridProps {
  /** Sets token for grid-template-columns */
  columns: string[];
  /** Sets token for grid-template-rows */
  rows: string[];
  /** Sets token for grid-template-columns, each array represents a row */
  areas: string[][];
}

export interface PanelLayoutProps extends ComponentProps<'div'> {
  slim: CssGridProps;
  small?: CssGridProps;
  medium?: CssGridProps;
  large?: CssGridProps;
  wide?: CssGridProps;
  className?: string;
  testMetadata?: TestMetadata;
}

const isCssGridEntryDefined = (
  entry: [string, CssGridProps | undefined],
): entry is [string, CssGridProps] => {
  const [, spec] = entry;
  return spec !== undefined;
};

const getTokensFromCssGridProp = (
  reflowpoint: string,
  prop: CssGridProps,
): Record<string, string> => {
  const areaRows = prop.areas.map((row) => `"${row.join(' ')}"`).join('\n');

  return {
    [`--sb-panel-layout-${reflowpoint}-areas`]: areaRows,
    [`--sb-panel-layout-${reflowpoint}-columns`]: prop.columns.join(' '),
    [`--sb-panel-layout-${reflowpoint}-rows`]: prop.rows.join(' '),
  };
};

const PanelLayout: FC<PanelLayoutProps> = ({
  children,
  className,
  style,
  slim,
  small,
  medium,
  large,
  wide,
  testMetadata,
}) => {
  const tokens = useMemo(
    () =>
      Object.entries({ slim, small, medium, large, wide })
        .filter(isCssGridEntryDefined)
        .reduce<Record<string, string>>(
          (tokens, [reflowpoint, spec]) => ({
            ...tokens,
            ...getTokensFromCssGridProp(reflowpoint, spec),
          }),
          {},
        ),
    [slim, small, medium, large, wide],
  );

  return (
    <div
      className={classNames('sb-panel-layout', className)}
      style={{ ...tokens, ...style }}
      {...testMetadata}
    >
      {children}
    </div>
  );
};

export default PanelLayout;
